<template>
  <div>
    <div class="back-btn-container">
      <back-btn />
      <h3 class="title-page">{{ $tc("contractHistory") }}</h3>
    </div>
    <div class="mdm-table">
      <div class="admin_container">
        <div class="d-flex justify-space-between mb-3">
          <v-btn
            v-if="histories.length"
            :to="{
              name: 'contract-history-item',
              query: { code: $route.query.code },
            }"
            color="primary"
            >{{ $tc("contractHistory") }}
          </v-btn>
        </div>
        <table-with-filter :headers="headers" :items="histories">
          <template #filter.code>
            <div class="pa-1">
              <custom-input-text
                @input="getHistoryWithFilter"
                v-model="historyFilter.code"
                clearable
                class="user_input no-margin"
              />
            </div>
          </template>
          <template #filter.contractStatus>
            <div class="pa-1">
              <custom-select
                @input="getHistoryWithFilter"
                :items="contractStatus"
                item-text="text"
                item-value="value"
                v-model="historyFilter.state"
                clearable
                class="user_input no-margin"
              />
            </div>
          </template>
          <template #filter.conclusionDate>
            <div class="pa-1 d-flex align-center">
              <span class="text--accent-1 blue-dark--text">
                {{ $t("from") }}</span
              >
              <custom-date-picker
                clearable
                :max="historyFilter.toDate || new Date()"
                @input="getHistoryWithFilter"
                v-model="historyFilter.fromDate"
                hide-details
                color="#E3EBF3"
                class="mr-1 ml-1 data_item data_item_calendar user_input"
                icon="mdi-calendar-month small"
              ></custom-date-picker>
              <span class="text--accent-1 blue-dark--text">
                {{ $t("to") }}</span
              >
              <custom-date-picker
                hide-details
                :start-day="false"
                :min="historyFilter.fromDate"
                :max="new Date()"
                @input="getHistoryWithFilter"
                v-model="historyFilter.toDate"
                clearable
                color="#E3EBF3"
                class="data_input_tw ml-2 data_item user_input"
                icon="mdi-calendar-month small"
              ></custom-date-picker>
            </div>
          </template>

          <template #item.guessReceiveDate="{ item }">{{
            item.guessReceiveDate | fullTimeParse
          }}</template>
          <template #item.conclusionDate="{ item }">{{
            item.conclusionDate | fullTimeParse
          }}</template>
          <template
            #item.creationDate="{
              /**
               * @type Object
               * @property creationDate {string}
               */
              item,
            }"
            >{{ item.creationDate | fullTimeParse }}</template
          >
          <template #item.updatedDate="{ item }">{{
            item.updatedDate | fullTimeParse
          }}</template>
          <template
            #item.contractStatus="{
              /**
               * @type Object
               * @property changes {Array<string>}
               */
              item,
            }"
          >
            <div :class="{ 'notify-text': item.changes.includes('state') }">
              <span class="notify-text-element">{{
                $t(`contractStatus.${item.state}`)
              }}</span>
            </div>
          </template>
          <template #item.action="{ item, index }">
            <v-btn
              v-if="index !== 0 && $can($permissions['RESTORE'])"
              class="new_style"
              @click="onRestore(index)"
            >
              <vue-icon icon="newTimeIcon"></vue-icon>
            </v-btn>
          </template>
        </table-with-filter>
      </div>
      <warning-dialog
        @apply="PrevChangeContract"
        @cancel="selectedHistoryIndex = null"
        v-model="warningDialog"
        description="doYouWantGoBack"
        yes-text="yes"
      ></warning-dialog>
    </div>
  </div>
</template>

<script>
import { debounce, getQueryByKey } from "@/utils/pureFunctions";
import BackBtn from "@/components/ui/BackBtn";
import TableWithFilter from "@/components/ui/table/TableWithFilter";
import { contractStatus } from "@/utils/constants";
import WarningDialog from "@/components/ui/prompt/warning-dialog";

export default {
  data() {
    return {
      selectedHistoryIndex: null,
      warningDialog: false,
      isShowFilter: false,
      historyFilter: {
        code: null,
        fromDate: null,
        toDate: null,
        supplierId: null,
        contractCurrentStatus: null,
      },
      histories: [],
    };
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t("contractNumber"),
          value: "code",
        },
        {
          text: this.$t("status"),
          value: "contractStatus",
        },
        {
          text: this.$t("updatedByUser"),
          value: "updatedByUser",
        },
        {
          text: this.$t("contractDate"),
          value: "creationDate",
        },
        {
          text: this.$t("itemConclusionDate"),
          value: "conclusionDate",
        },
        {
          text: this.$t("updatedDate"),
          value: "updatedDate",
        },
        {
          text: this.$t("action"),
          value: "action",
        },
      ];
    },
    contractStatus() {
      return Object.keys(contractStatus).map((item) => {
        return {
          text: this.$t(`contractStatus.${item}`),
          value: item,
        };
      });
    },
  },
  methods: {
    onRestore(index) {
      this.selectedHistoryIndex = index;
      this.warningDialog = true;
    },
    async PrevChangeContract() {
      this.showLoading();
      try {
        await this.$http.post("backend/previous-changes-contract", {
          objectId: this.histories[this.selectedHistoryIndex].id,
          revision: this.histories[this.selectedHistoryIndex].revision,
        });
        await this.getHistories();
      } catch (err) {
        this.setResponseError({ error: err });
      } finally {
        this.clearLoading();
      }
    },
    getHistoryWithFilter: debounce(function () {
      let query = this.historyFilter;
      this.$router.replace({ query }).catch((e) => {
        this.setResponseError({ error: e });
      });
      this.getHistoryWithLoading();
    }, 300),

    async getHistories() {
      const params = { ...this.historyFilter };
      try {
        let {
          data: { data },
        } = await this.$http.get("history/contracts", {
          params,
        });
        this.histories = data;
      } catch (e) {
        this.setResponseError({ error: e });
      }
    },
    async getHistoryWithLoading() {
      this.showLoading();
      await this.getHistories();
      this.clearLoading();
    },
  },
  components: {
    WarningDialog,
    TableWithFilter,
    BackBtn,
  },
  async created() {
    const { query } = this.$route;
    this.historyFilter = getQueryByKey(this.historyFilter, query);
    this.showLoading();
    await this.getHistories();
    this.clearLoading();
  },
};
</script>
